<template>
  <div class="custom-ui-input-wrapper">
    <div class="custom-ui-input-label-row" v-if="label || info">
      <span class="custom-ui-input-label" v-if="label" v-text="label" />
      <ui-field-info :text="info" />
    </div>
    <label class="custom-ui-input" :class="[classes, style, readonly ? 'disabled' : '']">
      <input
        name="ui-input"
        autocomplete="off"
        class="custom-ui-input-textbox"
        :type="type"
        v-model="model"
        :placeholder="placeholder"
        :readonly="readonly"
        :maxlength="maxLength > 0 ? maxLength : undefined"
        :class="type"
      />
      <span class="custom-ui-input-counter" v-if="maxLength > 0">
        {{ model ? (model as string).length : 0 }} / {{ maxLength }}
      </span>
      <span class="custom-ui-input-icon" v-if="hasValidation">
        <i class="success-icon fa-regular fa-circle-check" v-if="!anyError" />
        <i class="danger-icon fa-regular fa-circle-exclamation" v-else />
      </span>
    </label>
  </div>
</template>

<script setup lang="ts">
  import type { IUIInput } from '@/types/Components'
  import { computed } from 'vue'

  const {
    label = '',
    type = 'text',
    placeholder = '',
    anyError = false,
    hasValidation = false,
    maxLength = 0,
    readonly = false,
    size = 'sm',
    info = '',
  } = defineProps<IUIInput>()

  const model = defineModel<string | number | null>({ required: true })

  const style = computed(() => {
    if (hasValidation) {
      return anyError ? 'danger' : 'success'
    } else {
      return ''
    }
  })

  const classes = computed(() => [`size-${size}`])
</script>

<style lang="scss" scoped>
  .custom-ui-input-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    .custom-ui-input-label-row {
      @include flexible($align: center, $wrap: wrap);
      gap: 0.35rem;
      margin-bottom: 0.5rem;
      .custom-ui-input-label {
        @include column-label($margin: 0);
        width: auto;
      }
    }
    .custom-ui-input {
      display: flex;
      align-items: center;
      border: 1px solid $border-color;
      border-radius: 5px;
      transition: 0.2s all ease;
      position: relative;
      width: 100%;
      padding: calc(0.5em - 1px) 1em;
      background-color: $white;
      &.danger {
        border: 1px solid $danger;
      }
      &.success {
        border: 1px solid $success;
      }
      &.disabled {
        border: 1px solid $disabled;
        cursor: not-allowed;
        background-color: $cancel;
        .custom-ui-input-textbox {
          cursor: not-allowed;
        }
      }
      .custom-ui-input-textbox {
        width: 100%;
        border: none;
        outline: none;
        border-radius: 5px;
        color: $primary-text-color;
        background-color: transparent;
        &::placeholder {
          color: #ccc;
        }
        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus,
        &:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px white inset !important; /* arka planı beyaza boyar */
          box-shadow: 0 0 0 1000px white inset !important; /* Firefox uyumu */
          -webkit-text-fill-color: #000 !important; /* yazı rengini ayarlamak için */
          transition: background-color 5000s ease-in-out 0s; /* arka plan animasyonunu gizler */
        }
      }
      input[type='number']::-webkit-outer-spin-button,
      input[type='number']::-webkit-inner-spin-button {
        -webkit-appearance: none !important;
        margin: 0;
      }
      input[type='number'] {
        -moz-appearance: textfield;
        appearance: textfield;
      }
      .custom-ui-input-counter {
        position: absolute;
        background-color: #fff;
        right: 0.5rem;
        bottom: -0.325rem;
        z-index: 2;
        font-size: 0.625rem;
        padding: 0 0.25rem;
        color: #666;
      }
      .custom-ui-input-icon {
        display: flex;
        justify-content: center;
        align-items: center;
        .danger-icon {
          color: $danger;
        }
        .success-icon {
          color: $success;
        }
      }
    }
  }
</style>
